import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const FOCUS_LABELS = {
  arts_martiaux: "Arts martiaux",
  force_conditionnement: "Force & cond.",
  perte_poids: "Perte de poids",
  nutrition: "Nutrition",
  hybride: "Hybride",
};

const RISK_STYLES = {
  green: { label: "Sain", color: "bg-emerald-950 text-emerald-300 border-emerald-800" },
  yellow: { label: "Surveiller", color: "bg-amber-950 text-amber-300 border-amber-800" },
  red: { label: "À risque", color: "bg-red-950 text-red-300 border-red-800" },
};

const TABS = ["profil", "mesures", "entrainement", "nutrition", "rendez-vous", "notes"];

export default function CoachingClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profil");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/coaching/clients/${id}`);
      setData(r.data);
    } catch {
      toast.error("Client introuvable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [id]);

  const generatePortalSlug = async () => {
    try {
      const r = await api.post(`/coaching/clients/${id}/generate-portal-slug`);
      const fullUrl = r.data.portal_full_url || `${window.location.origin}${r.data.portal_url}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
      } catch {
        // clipboard may fail in some browsers — fallback to prompt
      }
      const sms = r.data.sms || {};
      if (sms.sent) {
        toast.success(`Lien généré + SMS envoyé au client 📲 (${sms.sid?.slice(0, 10)}…)`);
      } else if (sms.reason === "no_phone") {
        toast.warning("Lien généré 📋 (pas de SMS — client sans téléphone)");
      } else if (sms.reason === "twilio_not_configured") {
        toast.warning("Lien généré 📋 (Twilio non configuré, SMS skip)");
      } else if (sms.reason) {
        toast.warning(`Lien généré 📋 (SMS échec : ${sms.reason.slice(0, 50)})`);
      } else {
        toast.success("Lien portail généré et copié 📋");
      }
      window.prompt("Copie ce lien et envoie-le au client si besoin :", fullUrl);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération slug");
    }
  };

  const sendWelcome = async () => {
    try {
      const r = await api.post(`/coaching/clients/${id}/send-welcome`, { origin: window.location.origin });
      toast.success(`Courriel de bienvenue envoyé à ${r.data.email} ✉️`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi du courriel");
    }
  };

  if (loading || !data) {
    return <div className="text-zinc-500">Chargement…</div>;
  }

  const c = data.client;
  const risk = RISK_STYLES[c.risk_status] || RISK_STYLES.yellow;

  return (
    <div className="space-y-6" data-testid="coaching-client-detail">
      <Link to="/app/coaching-prive" className="text-xs text-zinc-500 hover:text-amber-400" data-testid="back-link">← Retour aux clients privés</Link>

      {/* Header */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">CLIENT PRIVÉ KAIZEN</div>
            <h1 className="text-4xl font-bold tracking-tight">{c.full_name}</h1>
            <div className="text-zinc-500 text-sm mt-2">
              {c.city && `${c.city} · `}{c.country}
              {c.email && ` · ${c.email}`}
              {c.phone && ` · ${c.phone}`}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                {FOCUS_LABELS[c.coaching_focus] || c.coaching_focus}
              </Badge>
              <Badge variant="outline" className={risk.color}>{risk.label}</Badge>
              {c.package && <Badge variant="outline" className="border-zinc-700">{c.package}</Badge>}
              {c.sessions_remaining != null && (
                <Badge variant="outline" className="border-amber-500/40 text-amber-300">{c.sessions_remaining} séances restantes</Badge>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat label="Séances faites" value={data.summary.sessions_done} />
            <MiniStat label="Mesures" value={data.summary.metrics_count} />
            <MiniStat label="Nutrition logs" value={data.summary.nutrition_count} />
          </div>
        </div>
        {/* Portail client */}
        <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">PORTAIL CLIENT (SELF-SERVICE)</div>
            {c.portal_slug ? (
              <a
                href={`/coaching-prive/portail/${c.portal_slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-emerald-400 hover:text-emerald-300 underline break-all"
                data-testid="portal-link-active"
              >
                {window.location.origin}/coaching-prive/portail/{c.portal_slug.slice(0, 12)}…
              </a>
            ) : (
              <div className="text-xs text-zinc-500">Aucun portail actif — génère un lien pour donner accès au client.</div>
            )}
            <div className="mt-1.5 text-[11px]" data-testid="welcome-status">
              {c.welcome_sent
                ? <span className="text-emerald-400">✅ Courriel de bienvenue envoyé</span>
                : <span className="text-zinc-500">✉️ Courriel de bienvenue non envoyé</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={sendWelcome}
              size="sm"
              className="bg-[#D90429] hover:bg-[#BF0424] text-white"
              data-testid="send-welcome-btn"
              disabled={!c.email}
              title={!c.email ? "Ajoute un courriel au client d'abord" : "Envoyer le courriel de bienvenue (avatar + portail + calendrier)"}
            >
              {c.welcome_sent ? "✉️ Renvoyer la bienvenue" : "✉️ Envoyer la bienvenue"}
            </Button>
            <Button
              onClick={generatePortalSlug}
              size="sm"
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="generate-portal-slug-btn"
            >
              {c.portal_slug ? "🔄 Régénérer lien portail" : "🔗 Générer lien portail"}
            </Button>
          </div>
        </div>
        {c.primary_goal && (
          <div className="mt-6 pt-6 border-t border-zinc-900">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">OBJECTIF PRINCIPAL</div>
            <div className="text-lg text-zinc-200">{c.primary_goal}</div>
            {c.target_date && <div className="text-xs text-zinc-500 mt-1">Échéance : {c.target_date}</div>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-2" data-testid="coaching-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={
              "px-4 py-2 rounded-full text-sm font-medium transition-all " +
              (tab === t
                ? "bg-amber-500 text-black"
                : "bg-zinc-950 text-zinc-300 border border-zinc-800 hover:border-amber-500/40")
            }
          >
            {{ profil: "Profil", mesures: "Mesures", entrainement: "Entraînement", nutrition: "Nutrition", "rendez-vous": "Rendez-vous", notes: "Notes" }[t]}
          </button>
        ))}
      </div>

      {tab === "profil" && <ProfilTab client={c} onReload={reload} />}
      {tab === "mesures" && <MesuresTab clientId={id} />}
      {tab === "entrainement" && <EntrainementTab clientId={id} />}
      {tab === "nutrition" && <NutritionTab clientId={id} onReload={reload} />}
      {tab === "rendez-vous" && <BookingsTab clientId={id} />}
      {tab === "notes" && <NotesTab clientId={id} />}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-zinc-300 text-xs mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3">
    <div className="text-2xl font-bold text-amber-300">{value}</div>
    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{label}</div>
  </div>
);

const ProfilTab = ({ client, onReload }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(client);

  const save = async () => {
    try {
      await api.patch(`/coaching/clients/${client.id}`, form);
      toast.success("Profil mis à jour");
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  if (!editing) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => { setForm(client); setEditing(true); }} data-testid="profil-edit">✏️ Modifier</Button>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <ReadField label="Objectif principal" value={client.primary_goal} />
          <ReadField label="Date cible" value={client.target_date} />
          <ReadField label="Forfait" value={client.package} />
          <ReadField label="Séances restantes" value={client.sessions_remaining} />
          <div className="sm:col-span-2"><ReadField label="Pourquoi personnel" value={client.why_personal} /></div>
          <div className="sm:col-span-2"><ReadField label="Obstacles connus" value={client.obstacles} /></div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom complet"><Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
        <Field label="Focus">
          <Select value={form.coaching_focus} onValueChange={(v) => setForm({ ...form, coaching_focus: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FOCUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Téléphone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Field label="Ville"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
        <Field label="Pays"><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
        <div className="sm:col-span-2"><Field label="Objectif principal"><Input value={form.primary_goal || ""} onChange={(e) => setForm({ ...form, primary_goal: e.target.value })} /></Field></div>
        <Field label="Date cible"><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></Field>
        <Field label="Forfait"><Input value={form.package || ""} onChange={(e) => setForm({ ...form, package: e.target.value })} /></Field>
        <Field label="Séances restantes"><Input type="number" value={form.sessions_remaining ?? ""} onChange={(e) => setForm({ ...form, sessions_remaining: parseInt(e.target.value) || null })} /></Field>
        <div className="sm:col-span-2"><Field label="Pourquoi personnel"><Textarea rows={3} value={form.why_personal || ""} onChange={(e) => setForm({ ...form, why_personal: e.target.value })} /></Field></div>
        <div className="sm:col-span-2"><Field label="Obstacles"><Textarea rows={2} value={form.obstacles || ""} onChange={(e) => setForm({ ...form, obstacles: e.target.value })} /></Field></div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="profil-save">Sauvegarder</Button>
        <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
      </div>
    </div>
  );
};

const ReadField = ({ label, value }) => (
  <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-zinc-200 mt-1">{value || <span className="text-zinc-600 italic">—</span>}</div>
  </div>
);

const MesuresTab = ({ clientId }) => {
  const [metrics, setMetrics] = useState([]);
  const [adding, setAdding] = useState(null);

  const reload = async () => {
    const r = await api.get(`/coaching/clients/${clientId}/metrics`);
    setMetrics(r.data.metrics || []);
  };
  useEffect(() => { reload(); }, [clientId]);

  const save = async () => {
    try {
      await api.post(`/coaching/clients/${clientId}/metrics`, adding);
      toast.success("Mesure ajoutée");
      setAdding(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setAdding({})} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="metrics-add">+ Ajouter une mesure</Button>
      <div className="grid gap-2">
        {metrics.length === 0 && <div className="text-zinc-500 text-sm italic">Aucune mesure encore.</div>}
        {metrics.map((m) => (
          <div key={m.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`metric-${m.id}`}>
            <div className="text-xs text-zinc-500 mb-2">{m.measured_at?.slice(0, 16).replace("T", " ")}</div>
            <div className="grid sm:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
              {m.weight_kg != null && <MetricVal label="Poids" v={`${m.weight_kg} kg`} />}
              {m.waist_cm != null && <MetricVal label="Tour taille" v={`${m.waist_cm} cm`} />}
              {m.hips_cm != null && <MetricVal label="Hanches" v={`${m.hips_cm} cm`} />}
              {m.chest_cm != null && <MetricVal label="Poitrine" v={`${m.chest_cm} cm`} />}
              {m.arm_cm != null && <MetricVal label="Bras" v={`${m.arm_cm} cm`} />}
              {m.thigh_cm != null && <MetricVal label="Cuisse" v={`${m.thigh_cm} cm`} />}
              {m.resting_hr != null && <MetricVal label="FC repos" v={`${m.resting_hr} bpm`} />}
              {m.sleep_hours != null && <MetricVal label="Sommeil" v={`${m.sleep_hours} h`} />}
              {m.energy_1_10 != null && <MetricVal label="Énergie" v={`${m.energy_1_10}/10`} />}
              {m.stress_1_10 != null && <MetricVal label="Stress" v={`${m.stress_1_10}/10`} />}
            </div>
            {m.note && <div className="text-xs text-zinc-400 mt-2 italic">{m.note}</div>}
          </div>
        ))}
      </div>

      {adding && (
        <Dialog open onOpenChange={(v) => !v && setAdding(null)}>
          <DialogContent className="max-w-2xl" data-testid="metrics-add-dialog">
            <DialogHeader><DialogTitle>Nouvelle mesure</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-3 gap-3 py-2">
              <Field label="Poids (kg)"><Input type="number" step="0.1" value={adding.weight_kg ?? ""} onChange={(e) => setAdding({ ...adding, weight_kg: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Tour taille (cm)"><Input type="number" step="0.1" value={adding.waist_cm ?? ""} onChange={(e) => setAdding({ ...adding, waist_cm: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Hanches (cm)"><Input type="number" step="0.1" value={adding.hips_cm ?? ""} onChange={(e) => setAdding({ ...adding, hips_cm: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Poitrine (cm)"><Input type="number" step="0.1" value={adding.chest_cm ?? ""} onChange={(e) => setAdding({ ...adding, chest_cm: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Bras (cm)"><Input type="number" step="0.1" value={adding.arm_cm ?? ""} onChange={(e) => setAdding({ ...adding, arm_cm: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Cuisse (cm)"><Input type="number" step="0.1" value={adding.thigh_cm ?? ""} onChange={(e) => setAdding({ ...adding, thigh_cm: parseFloat(e.target.value) || null })} /></Field>
              <Field label="FC repos (bpm)"><Input type="number" value={adding.resting_hr ?? ""} onChange={(e) => setAdding({ ...adding, resting_hr: parseInt(e.target.value) || null })} /></Field>
              <Field label="Sommeil (h)"><Input type="number" step="0.1" value={adding.sleep_hours ?? ""} onChange={(e) => setAdding({ ...adding, sleep_hours: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Énergie (1-10)"><Input type="number" min="1" max="10" value={adding.energy_1_10 ?? ""} onChange={(e) => setAdding({ ...adding, energy_1_10: parseInt(e.target.value) || null })} /></Field>
              <Field label="Stress (1-10)"><Input type="number" min="1" max="10" value={adding.stress_1_10 ?? ""} onChange={(e) => setAdding({ ...adding, stress_1_10: parseInt(e.target.value) || null })} /></Field>
              <div className="sm:col-span-3"><Field label="Note"><Textarea rows={2} value={adding.note || ""} onChange={(e) => setAdding({ ...adding, note: e.target.value })} /></Field></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(null)}>Annuler</Button>
              <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="metric-save">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const MetricVal = ({ label, v }) => (
  <div className="bg-black border border-zinc-900 rounded px-3 py-2">
    <div className="text-[10px] uppercase text-zinc-500">{label}</div>
    <div className="text-amber-300 font-semibold">{v}</div>
  </div>
);

const EntrainementTab = ({ clientId }) => {
  const [sessions, setSessions] = useState([]);
  const [adding, setAdding] = useState(null);

  const reload = async () => {
    const r = await api.get(`/coaching/clients/${clientId}/sessions`);
    setSessions(r.data.sessions || []);
  };
  useEffect(() => { reload(); }, [clientId]);

  const save = async () => {
    if (!adding.title) { toast.error("Titre requis"); return; }
    try {
      await api.post(`/coaching/clients/${clientId}/sessions`, adding);
      toast.success("Séance créée");
      setAdding(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const setStatus = async (sid, status) => {
    try {
      await api.patch(`/coaching/clients/${clientId}/sessions/${sid}`, { status });
      toast.success("Statut mis à jour");
      reload();
    } catch { toast.error("Échec"); }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setAdding({ exercises: [] })} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="sessions-add">+ Nouvelle séance</Button>
      <div className="space-y-3">
        {sessions.length === 0 && <div className="text-zinc-500 text-sm italic">Aucune séance encore.</div>}
        {sessions.map((s) => (
          <div key={s.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`session-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{s.title}</div>
                <div className="text-xs text-zinc-500 mt-1">{s.scheduled_at?.slice(0, 16).replace("T", " ")}</div>
                {s.program_label && <div className="text-xs text-amber-400 mt-1">{s.program_label}</div>}
                {s.coach_notes && <div className="text-xs text-zinc-400 mt-2 italic">{s.coach_notes}</div>}
              </div>
              <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid={`session-${s.id}-status`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_faire">À faire</SelectItem>
                  <SelectItem value="fait">✅ Fait</SelectItem>
                  <SelectItem value="partiel">⏸ Partiel</SelectItem>
                  <SelectItem value="remis">↻ Remis</SelectItem>
                  <SelectItem value="annule">❌ Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {adding && (
        <Dialog open onOpenChange={(v) => !v && setAdding(null)}>
          <DialogContent className="max-w-2xl" data-testid="session-add-dialog">
            <DialogHeader><DialogTitle>Nouvelle séance d'entraînement</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Titre *"><Input value={adding.title || ""} onChange={(e) => setAdding({ ...adding, title: e.target.value })} placeholder="ex: Force jambes — Phase 2" /></Field>
              <Field label="Programme / Label"><Input value={adding.program_label || ""} onChange={(e) => setAdding({ ...adding, program_label: e.target.value })} placeholder="ex: KAIZEN Power Cycle" /></Field>
              <Field label="Date / Heure"><Input type="datetime-local" value={adding.scheduled_at?.slice(0, 16) || ""} onChange={(e) => setAdding({ ...adding, scheduled_at: e.target.value })} /></Field>
              <Field label="Notes coach"><Textarea rows={4} value={adding.coach_notes || ""} onChange={(e) => setAdding({ ...adding, coach_notes: e.target.value })} placeholder="Exercices détaillés, intensité, focus de la séance…" /></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(null)}>Annuler</Button>
              <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="session-save">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const NutritionTab = ({ clientId, onReload }) => {
  const [logs, setLogs] = useState([]);
  const [adding, setAdding] = useState(null);

  const reload = async () => {
    const r = await api.get(`/coaching/clients/${clientId}/nutrition`);
    setLogs(r.data.logs || []);
  };
  useEffect(() => { reload(); }, [clientId]);

  const save = async () => {
    if (!adding.adherence_1_10) { toast.error("Note d'adhérence requise"); return; }
    try {
      await api.post(`/coaching/clients/${clientId}/nutrition`, adding);
      toast.success("Journal nutrition enregistré");
      setAdding(null);
      reload();
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setAdding({ adherence_1_10: 7 })} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="nutrition-add">+ Journal nutrition</Button>
      <div className="grid gap-2">
        {logs.length === 0 && <div className="text-zinc-500 text-sm italic">Aucun journal nutrition encore.</div>}
        {logs.map((l) => (
          <div key={l.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`nutrition-${l.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="text-xs text-zinc-500">{l.log_date?.slice(0, 10)}</div>
              <Badge variant="outline" className={l.adherence_1_10 >= 7 ? "border-emerald-500/40 text-emerald-300" : l.adherence_1_10 >= 4 ? "border-amber-500/40 text-amber-300" : "border-red-500/40 text-red-300"}>
                Adhérence : {l.adherence_1_10}/10
              </Badge>
              {l.water_litres != null && <span className="text-xs text-blue-400">💧 {l.water_litres} L</span>}
            </div>
            {l.meals_notes && <div className="text-sm text-zinc-300">{l.meals_notes}</div>}
          </div>
        ))}
      </div>

      {adding && (
        <Dialog open onOpenChange={(v) => !v && setAdding(null)}>
          <DialogContent data-testid="nutrition-add-dialog">
            <DialogHeader><DialogTitle>Nouveau journal nutrition</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Date"><Input type="date" value={adding.log_date || ""} onChange={(e) => setAdding({ ...adding, log_date: e.target.value })} /></Field>
              <Field label="Adhérence (1-10) *"><Input type="number" min="1" max="10" value={adding.adherence_1_10 || ""} onChange={(e) => setAdding({ ...adding, adherence_1_10: parseInt(e.target.value) || null })} /></Field>
              <Field label="Eau (L)"><Input type="number" step="0.1" value={adding.water_litres ?? ""} onChange={(e) => setAdding({ ...adding, water_litres: parseFloat(e.target.value) || null })} /></Field>
              <Field label="Notes repas"><Textarea rows={4} value={adding.meals_notes || ""} onChange={(e) => setAdding({ ...adding, meals_notes: e.target.value })} placeholder="Petit-déj, dîner, souper, collations, écarts, sensations…" /></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(null)}>Annuler</Button>
              <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="nutrition-save">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const BookingsTab = ({ clientId }) => {
  const [bookings, setBookings] = useState([]);
  const [adding, setAdding] = useState(null);

  const reload = async () => {
    const r = await api.get(`/coaching/clients/${clientId}/bookings`);
    setBookings(r.data.bookings || []);
  };
  useEffect(() => { reload(); }, [clientId]);

  const save = async () => {
    if (!adding.scheduled_at) { toast.error("Date requise"); return; }
    try {
      await api.post(`/coaching/clients/${clientId}/bookings`, adding);
      toast.success("Rendez-vous créé");
      setAdding(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const setStatus = async (bid, status) => {
    try {
      await api.patch(`/coaching/clients/${clientId}/bookings/${bid}`, { status });
      toast.success("Statut mis à jour");
      reload();
    } catch { toast.error("Échec"); }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setAdding({ duration_min: 60, location: "dojo" })} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="bookings-add">+ Nouveau rendez-vous</Button>
      <div className="space-y-3">
        {bookings.length === 0 && <div className="text-zinc-500 text-sm italic">Aucun rendez-vous.</div>}
        {bookings.map((b) => (
          <div key={b.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`booking-${b.id}`}>
            <div>
              <div className="font-semibold">{b.scheduled_at?.slice(0, 16).replace("T", " ")}</div>
              <div className="text-xs text-zinc-500 mt-1">{b.duration_min} min · {b.location}</div>
              {b.note && <div className="text-xs text-zinc-400 mt-2 italic">{b.note}</div>}
            </div>
            <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid={`booking-${b.id}-status`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirme">Confirmé</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {adding && (
        <Dialog open onOpenChange={(v) => !v && setAdding(null)}>
          <DialogContent data-testid="booking-add-dialog">
            <DialogHeader><DialogTitle>Nouveau rendez-vous</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Date / Heure *"><Input type="datetime-local" value={adding.scheduled_at || ""} onChange={(e) => setAdding({ ...adding, scheduled_at: e.target.value })} /></Field>
              <Field label="Durée (min)"><Input type="number" value={adding.duration_min || 60} onChange={(e) => setAdding({ ...adding, duration_min: parseInt(e.target.value) || 60 })} /></Field>
              <Field label="Lieu">
                <Select value={adding.location || "dojo"} onValueChange={(v) => setAdding({ ...adding, location: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dojo">🥋 Dojo</SelectItem>
                    <SelectItem value="visio">💻 Visio</SelectItem>
                    <SelectItem value="exterieur">🌳 Extérieur</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Note"><Textarea rows={2} value={adding.note || ""} onChange={(e) => setAdding({ ...adding, note: e.target.value })} /></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(null)}>Annuler</Button>
              <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="booking-save">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const NotesTab = ({ clientId }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  const reload = async () => {
    const r = await api.get(`/coaching/clients/${clientId}/notes`);
    setNotes(r.data.notes || []);
  };
  useEffect(() => { reload(); }, [clientId]);

  const save = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/coaching/clients/${clientId}/notes`, { note: newNote, visible_to_client: false });
      toast.success("Note ajoutée");
      setNewNote("");
      reload();
    } catch { toast.error("Échec"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
        <Textarea
          rows={3}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Observation, ajustement à prévoir, mood, fait marquant…"
          data-testid="notes-input"
        />
        <Button onClick={save} className="bg-amber-500 text-black hover:bg-amber-400 mt-2" data-testid="notes-save">
          + Enregistrer la note
        </Button>
      </div>
      <div className="space-y-3">
        {notes.length === 0 && <div className="text-zinc-500 text-sm italic">Aucune note encore.</div>}
        {notes.map((n) => (
          <div key={n.id} className="bg-zinc-950 border-l-2 border-amber-500/50 rounded-r-lg p-4 pl-5" data-testid={`note-${n.id}`}>
            <div className="text-xs text-zinc-500 mb-2">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
            <div className="text-sm text-zinc-200 whitespace-pre-wrap">{n.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
